/**
 * FlashcardPlayer — learner-facing spaced-repetition card runtime.
 *
 * Renders a single flashcard scene (scene_type="flashcard") with:
 *   - front_content shown on card face (markdown rendered inline)
 *   - optional hint_text collapsible section ("Need a hint?")
 *   - "Show Answer" button flips the card (180° Y-axis CSS animation, ~400ms)
 *   - After flip: back_content + key_insight highlighted in distinct style
 *   - Confidence rating buttons: Again | Hard | Good | Easy
 *   - On rating: POST /scenario-os/adaptive/review/{learner_id}/{skill_id}/rate
 *   - After rating: emits 'flashcard_reviewed' learner event
 *   - Auto-advances to next card after 1.5s, or immediately on "Next" button
 *
 * Props:
 *   scene       {object}   — flashcard scene document (domain_metadata.*)
 *   learnerId   {string}   — learner identifier (from auth token / session)
 *   onComplete  {function} — called when learner submits a rating
 */

import React, { useState, useCallback, useRef } from 'react';
import { emitLearnerEvent } from '../../services/learnerEvents';

const API_BASE = '/scenario-os';
const TOKEN_KEY = 'scenario_os_token';

function getToken() {
  return (
    localStorage.getItem(TOKEN_KEY) ||
    sessionStorage.getItem(TOKEN_KEY) ||
    ''
  );
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Minimal markdown → HTML: bold, italic, code, line-breaks only.
function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code style="background:#f0f4ff;padding:1px 4px;border-radius:3px">$1</code>')
    .replace(/\n/g, '<br />');
}

// ── Confidence rating config ──────────────────────────────────────────────────
const RATINGS = [
  { label: 'Again', value: 1, color: '#e53e3e', bg: '#fff5f5' },
  { label: 'Hard',  value: 2, color: '#dd6b20', bg: '#fffaf0' },
  { label: 'Good',  value: 3, color: '#38a169', bg: '#f0fff4' },
  { label: 'Easy',  value: 4, color: '#3182ce', bg: '#ebf8ff' },
];

export default function FlashcardPlayer({ scene, learnerId, onComplete }) {
  const meta = (scene && scene.domain_metadata) || scene || {};
  const {
    front_content = '',
    back_content = '',
    key_insight = '',
    hint_text,
    skill_id = '',
    media_url,
  } = meta;

  const [flipped, setFlipped] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [rated, setRated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const advanceTimer = useRef(null);

  const handleShowAnswer = useCallback(() => {
    setFlipped(true);
  }, []);

  const handleRate = useCallback(
    async (ratingValue, ratingLabel) => {
      if (rated || submitting) return;
      setSubmitting(true);

      // POST FSRS rating; capture next_review_date from response
      let nextReviewDate = null;
      try {
        const resp = await fetch(
          `${API_BASE}/adaptive/review/${encodeURIComponent(learnerId)}/${encodeURIComponent(skill_id)}/rate`,
          {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ confidence: ratingValue, time_taken_seconds: 0 }),
          }
        );
        if (resp.ok) {
          const data = await resp.json().catch(() => ({}));
          nextReviewDate = data.next_eval_date || null;
        }
      } catch (_e) {
        // non-blocking — proceed with event emission
      }

      // Emit flashcard_reviewed learner event
      await emitLearnerEvent({
        event_type: 'flashcard_reviewed',
        skill_id,
        domain: scene.domain_id || '',
        rating: ratingLabel.toLowerCase(),
        rating_value: ratingValue,
        scene_id: scene.scene_id || '',
        next_review_date: nextReviewDate,
      });

      setRated(true);
      setSubmitting(false);

      // Auto-advance after 1.5 s
      if (onComplete) {
        advanceTimer.current = setTimeout(() => {
          onComplete({ rating: ratingValue, ratingLabel });
        }, 1500);
      }
    },
    [rated, submitting, learnerId, skill_id, scene, onComplete]
  );

  const handleNext = useCallback(() => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    if (onComplete) onComplete({ rating: null, ratingLabel: null });
  }, [onComplete]);

  return (
    <div style={containerStyle}>
      {/* ── Card face ──────────────────────────────────────────────────── */}
      <div style={cardWrapStyle}>
        <div style={{ ...cardStyle, transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>

          {/* Front */}
          <div style={cardFaceStyle}>
            {media_url && (
              <img
                src={media_url}
                alt="Card media"
                style={mediaStyle}
              />
            )}
            <div
              style={frontContentStyle}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(front_content) }}
            />
            {hint_text && (
              <div style={hintContainerStyle}>
                <button
                  style={hintToggleStyle}
                  onClick={() => setHintOpen((o) => !o)}
                >
                  {hintOpen ? '▾ Hide hint' : '▸ Need a hint?'}
                </button>
                {hintOpen && (
                  <div
                    style={hintTextStyle}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(hint_text) }}
                  />
                )}
              </div>
            )}
            {!flipped && (
              <button style={showAnswerBtnStyle} onClick={handleShowAnswer}>
                Show Answer
              </button>
            )}
          </div>

          {/* Back (visible after flip) */}
          {flipped && (
            <div style={{ ...cardFaceStyle, ...cardBackStyle }}>
              <div
                style={backContentStyle}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(back_content) }}
              />
              {key_insight && (
                <div style={insightBoxStyle}>
                  <span style={insightLabelStyle}>Key Insight</span>
                  <div
                    style={insightTextStyle}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(key_insight) }}
                  />
                </div>
              )}
              {/* Rating buttons */}
              {!rated ? (
                <div style={ratingsRowStyle}>
                  {RATINGS.map((r) => (
                    <button
                      key={r.value}
                      disabled={submitting}
                      style={{ ...ratingBtnStyle, color: r.color, background: r.bg, borderColor: r.color }}
                      onClick={() => handleRate(r.value, r.label)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={ratedFeedbackStyle}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      background: '#f0fff4',
                      border: '1px solid #68d391',
                      borderRadius: 20,
                      padding: '4px 12px',
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#276749',
                    }}
                  >
                    ✓ Reviewed
                  </span>
                  <button style={nextBtnStyle} onClick={handleNext}>
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px 16px',
  minHeight: '300px',
  boxSizing: 'border-box',
};

const cardWrapStyle = {
  width: '100%',
  maxWidth: '640px',
  perspective: '1000px',
};

const cardStyle = {
  position: 'relative',
  width: '100%',
  minHeight: '280px',
  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  transformStyle: 'preserve-3d',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
};

const cardFaceStyle = {
  width: '100%',
  minHeight: '280px',
  backfaceVisibility: 'hidden',
  background: '#fff',
  borderRadius: '12px',
  padding: '28px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxSizing: 'border-box',
};

const cardBackStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  transform: 'rotateY(180deg)',
  background: '#fafbff',
};

const mediaStyle = {
  width: '100%',
  maxHeight: '180px',
  objectFit: 'contain',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
};

const frontContentStyle = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#1a202c',
  lineHeight: 1.6,
};

const hintContainerStyle = { marginTop: 4 };

const hintToggleStyle = {
  background: 'none',
  border: 'none',
  color: '#718096',
  cursor: 'pointer',
  fontSize: '13px',
  padding: '4px 0',
};

const hintTextStyle = {
  marginTop: '8px',
  padding: '10px 14px',
  background: '#fffbeb',
  border: '1px solid #f6e05e',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#744210',
};

const showAnswerBtnStyle = {
  marginTop: 'auto',
  padding: '12px 32px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  alignSelf: 'center',
};

const backContentStyle = {
  fontSize: '16px',
  color: '#2d3748',
  lineHeight: 1.7,
};

const insightBoxStyle = {
  padding: '14px 16px',
  background: 'linear-gradient(135deg, #ebf4ff 0%, #f0f4ff 100%)',
  border: '1px solid #c3dafe',
  borderRadius: '8px',
};

const insightLabelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#4c51bf',
  marginBottom: '6px',
};

const insightTextStyle = {
  fontSize: '14px',
  color: '#434190',
  lineHeight: 1.6,
};

const ratingsRowStyle = {
  display: 'flex',
  gap: '8px',
  marginTop: 'auto',
  flexWrap: 'wrap',
};

const ratingBtnStyle = {
  flex: '1 1 80px',
  padding: '10px 8px',
  border: '1.5px solid',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'opacity 0.15s',
};

const ratedFeedbackStyle = {
  marginTop: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontSize: '15px',
  color: '#38a169',
  fontWeight: 500,
};

const nextBtnStyle = {
  background: 'none',
  border: '1px solid #38a169',
  color: '#38a169',
  borderRadius: '6px',
  padding: '6px 16px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};
